import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { MultilineInput } from "@/components/ui/multiline-input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useToast } from "@/components/ui/use-toast";
import {
  InAppNotification,
  useNewTeenNotifications,
  useSendFirstMessage,
} from "@/lib/api/notifications";
import { useMentorView } from "@/lib/hooks/admin/use-mentor-view";
import { useCurrentConversation } from "@/lib/hooks/conversations/use-current-conversation";
import { useEvents } from "@/lib/hooks/use-events";
import {
  getMenteeDisplayName,
  isFieldRedacted,
  REDACTED_LABEL,
  renderRedactableField,
} from "@/lib/redacted-display";
import { cn, getAge } from "@/lib/utils";
import { StopwatchIcon } from "@radix-ui/react-icons";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import {
  Bot,
  CheckCircle,
  ExternalLink,
  Eye,
  EyeOff,
  Loader2,
  Send,
  Users,
} from "lucide-react";
import { useEffect, useState } from "react";
import { PostHogEvents, useTrackPage } from "@/lib/posthog-events";
import { requirePermission } from "@/lib/casl/route-guards";

export const Route = createFileRoute("/_authenticated/new-teens/")({
  component: NewTeensPage,
  beforeLoad: ({ context }) => {
    requirePermission(context.user, "read", "mentorships");
  },
});

function NewTeenCardSkeleton() {
  return (
    <Card className="overflow-hidden">
      <div className="space-y-4 p-6">
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-4 w-32" />
          </div>
          <Skeleton className="h-6 w-16" />
        </div>
        <div className="grid grid-cols-2 gap-8">
          <div className="space-y-2">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-4 w-40" />
          </div>
          <div className="space-y-2">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-16 w-full" />
            <div className="flex gap-2">
              <Skeleton className="h-6 w-20" />
              <Skeleton className="h-6 w-16" />
              <Skeleton className="h-6 w-24" />
            </div>
          </div>
        </div>
        <div className="space-y-2">
          <Skeleton className="h-20 w-full" />
          <div className="flex gap-2">
            <Skeleton className="h-9 flex-1" />
            <Skeleton className="h-9 flex-1" />
          </div>
        </div>
      </div>
    </Card>
  );
}

interface NewTeenCardProps {
  notification: InAppNotification;
}

function NewTeenCard({ notification }: NewTeenCardProps) {
  const [message, setMessage] = useState("");
  const { toast } = useToast();
  const sendFirstMessage = useSendFirstMessage();
  const { setMentorView } = useMentorView();
  const { setCurrentConversation } = useCurrentConversation();
  const navigate = useNavigate();

  const { data: notificationData } = notification;
  const mentee = notificationData?.mentee;
  const mentorshipId = notificationData?.mentorshipId;

  if (!mentee || !mentorshipId) return null;

  const menteeRedactedFields = mentee.redacted_fields;
  const profileRedactedFields = mentee.profile?.redacted_fields;
  const isNameRedacted =
    isFieldRedacted("first_name", menteeRedactedFields, mentee.first_name) ||
    isFieldRedacted("last_name", menteeRedactedFields, mentee.last_name) ||
    isFieldRedacted("name", menteeRedactedFields, mentee.name);
  const teenName = isNameRedacted
    ? getMenteeDisplayName(mentee)
    : `${mentee.first_name ?? ""} ${mentee.last_name ?? ""}`.trim();
  const isBirthdayRedacted = isFieldRedacted(
    "birthday",
    menteeRedactedFields,
    mentee.birthday,
  );
  const teenInitial = isNameRedacted
    ? "?"
    : mentee.first_name?.[0]?.toUpperCase() || "T";

  const formatBirthdayWithAge = (birthday?: Date | string | null) => {
    if (!birthday) return "Unknown age";
    const birth = new Date(birthday);
    const age = getAge(birth.toISOString());
    const formattedDate = birth.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    });
    return `${formattedDate} (${age} y/o)`;
  };

  // Get real interests and quote from teen profile data
  const interests = mentee.profile?.interests || [];
  const interestDetails = mentee.profile?.metadata?.interestDetails || {};

  // Get interest details as quote - pick the first available one
  const interestQuote =
    interestDetails && Object.keys(interestDetails).length > 0
      ? Object.values(interestDetails)[0]
      : undefined;

  const getStatus = () => {
    console.log("🔍 getStatus - notificationData:", notificationData);
    console.log("🔍 mentorReplied:", notificationData?.mentorReplied);
    console.log(
      "🔍 automatedMessageSent:",
      notificationData?.automatedMessageSent,
    );

    if (notificationData?.mentorReplied) return "replied";
    if (notificationData?.automatedMessageSent) return "automated";
    return "new";
  };

  const getStatusBadge = () => {
    const status = getStatus();
    switch (status) {
      case "replied":
        return (
          <Badge variant="secondary" className="text-xs font-normal">
            Replied
          </Badge>
        );
      case "automated":
        return (
          <Badge variant="secondary" className="text-xs font-normal">
            Automated
          </Badge>
        );
      default:
        return (
          <Badge className="bg-black text-xs font-normal text-white">New</Badge>
        );
    }
  };

  const handleSendMessage = async () => {
    if (!message.trim()) return;

    console.log("🚀 Starting to send message...");
    console.log(
      "🚀 sendFirstMessage.isPending before:",
      sendFirstMessage.isPending,
    );

    try {
      const result = await sendFirstMessage.mutateAsync({
        notificationId: notification.id,
        message: message.trim(),
        mentorshipId,
      });

      console.log("✅ Message sent successfully:", result);
      console.log(
        "✅ sendFirstMessage.isPending after:",
        sendFirstMessage.isPending,
      );

      toast({
        title: "Message sent!",
        description: `Your message was sent to ${teenName}`,
      });

      setMessage("");
    } catch (error) {
      console.error("❌ Failed to send message:", error);
      console.log(
        "❌ sendFirstMessage.isPending after error:",
        sendFirstMessage.isPending,
      );
      toast({
        title: "Failed to send message",
        description: "Please try again later",
        variant: "destructive",
      });
    }
  };

  const handleGoToChat = () => {
    // Extract channel URL from notification data
    const channelUrl = notificationData?.sdkUrl;

    if (!channelUrl) {
      toast({
        title: "Cannot open chat",
        description: "Channel URL not found",
        variant: "destructive",
      });
      return;
    }

    // Enable mentor view first
    setMentorView(true);

    // Set the current conversation
    setCurrentConversation(channelUrl);

    // Navigate to conversations page
    navigate({ to: "/conversations" });
  };

  const isDisabled = getStatus() !== "new";

  return (
    <Card
      className={cn(
        "border border-gray-200 transition-all duration-200",
        getStatus() === "new" ? "bg-blue-50" : "bg-green-50",
      )}
    >
      <CardContent className="p-4">
        {/* Header */}
        <div className="mb-4 flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 text-xs font-medium text-gray-600">
              {teenInitial}
            </div>
            <div>
              <h2 className="font-semibold text-gray-900">{teenName}</h2>
              <div className="text-xs text-gray-500">
                Joined {format(new Date(notification.created_at), "MMM d")}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={handleGoToChat}
                    aria-label="Open Chat"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Open Chat</TooltipContent>
              </Tooltip>
            </TooltipProvider>
            {getStatusBadge()}
          </div>
        </div>

        {/* Content */}
        <div className="mb-4 space-y-3">
          <div className="flex gap-6 text-xs">
            <div>
              <span className="font-medium">
                {isBirthdayRedacted
                  ? REDACTED_LABEL
                  : formatBirthdayWithAge(mentee.birthday)}
              </span>
            </div>
            {!isFieldRedacted(
              "pronouns",
              menteeRedactedFields,
              mentee.pronouns,
            ) &&
              mentee.pronouns && (
              <div>
                <span className="text-gray-500">Pronouns: </span>
                <span className="font-medium">
                  {renderRedactableField(
                    "pronouns",
                    mentee.pronouns,
                    menteeRedactedFields,
                    "",
                  )}
                </span>
              </div>
            )}
            {!isFieldRedacted(
              "sex",
              profileRedactedFields,
              mentee.profile?.sex,
            ) &&
              mentee.profile?.sex && (
              <div>
                <span className="text-gray-500">Sex: </span>
                <span className="font-medium">
                  {renderRedactableField(
                    "sex",
                    mentee.profile?.sex,
                    profileRedactedFields,
                    "",
                  )}
                </span>
              </div>
            )}
          </div>

          <div>
            <h3 className="mb-2 text-sm font-medium text-gray-900">
              Their interests
            </h3>
            {interestQuote && (
              <p className="mb-2 text-xs italic text-gray-600">
                &ldquo;{interestQuote}&rdquo;
              </p>
            )}
            <div className="flex flex-wrap gap-1.5">
              {interests.length > 0 ? (
                <>
                  {interests.slice(0, 5).map((interest, index) => (
                    <Badge
                      key={index}
                      variant="secondary"
                      className="rounded-full px-2.5 py-0.5 text-xs font-normal"
                      title={interestDetails[interest] || interest}
                    >
                      {interest}
                    </Badge>
                  ))}
                  {interests.length > 5 && (
                    <Badge
                      variant="secondary"
                      className="rounded-full px-2.5 py-0.5 text-xs font-normal"
                    >
                      +{interests.length - 5} more
                    </Badge>
                  )}
                </>
              ) : (
                <Badge
                  variant="outline"
                  className="rounded-full px-2.5 py-0.5 text-xs font-normal text-gray-500"
                >
                  No interests listed yet
                </Badge>
              )}
            </div>
          </div>
        </div>

        {/* Action Section */}
        <div className="border-t pt-4">
          {!isDisabled ? (
            <div className="space-y-3">
              {/* Automated Message Notice */}
              <div className="rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 p-3">
                <div className="flex items-center gap-2 text-xs text-gray-600">
                  <StopwatchIcon className="h-4 w-4" />
                  <span>
                    If teen finishes onboarding an automated message will be
                    sent
                  </span>
                </div>
              </div>
              <MultilineInput
                placeholder="Write your first message..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                disabled={sendFirstMessage.isPending}
                endIcon={
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 rounded-full p-0"
                    onClick={handleSendMessage}
                    disabled={!message.trim() || sendFirstMessage.isPending}
                    aria-label="Send message"
                    title="Send message"
                  >
                    {sendFirstMessage.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                  </Button>
                }
              />
            </div>
          ) : (
            <div className="py-2">
              <div className="flex items-center gap-2 text-xs text-gray-600">
                {getStatus() === "replied" ? (
                  <CheckCircle className="h-4 w-4 text-green-600" />
                ) : (
                  <Bot className="h-4 w-4 text-gray-700" />
                )}
                <span className="font-medium">
                  {getStatus() === "replied"
                    ? "Message sent"
                    : "Automated message sent"}
                </span>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default function NewTeensPage() {
  const [showContacted, setShowContacted] = useState(false);
  const {
    data,
    isLoading,
    error,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useNewTeenNotifications();
  const { addEventListener } = useEvents();
  const queryClient = useQueryClient();
  useTrackPage(PostHogEvents.PAGE_NEW_TEENS);

  // Listen for automated message sent events to refresh the list
  useEffect(() => {
    const handleAutomatedMessageSent = (payload: {
      mentorId: string;
      menteeId: string;
      timestamp: string;
    }) => {
      console.log("🤖 AUTOMATED_MSG_SENT event received:", payload);

      // Invalidate and refetch the new teen notifications to get updated data
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey?.[0] === "notifications",
      });
    };

    const cleanup = addEventListener(
      "AUTOMATED_MSG_SENT",
      handleAutomatedMessageSent,
    );

    return cleanup;
  }, [addEventListener, queryClient]);

  if (error) {
    return (
      <div className="container mx-auto py-6">
        <div className="mb-6">
          <h1 className="text-3xl font-bold">New Teens</h1>
          <p className="text-muted-foreground">
            Manage and respond to new teen assignments
          </p>
        </div>
        <div className="text-center text-red-600">
          Failed to load new teens. Please try again later.
        </div>
      </div>
    );
  }

  const notifications = data?.pages.flatMap((page) => page.notifications) || [];

  // Filter notifications based on showContacted state
  const filteredNotifications = showContacted
    ? notifications
    : notifications.filter((notification) => {
        const data = notification.data;
        return !data?.mentorReplied && !data?.automatedMessageSent;
      });

  const totalCount = data?.pages[0]?.pagination?.total || 0;

  console.log(
    "Notifications",
    notifications,
    "Filtered:",
    filteredNotifications,
    "Total:",
    totalCount,
    "HasNext:",
    hasNextPage,
    isLoading,
    error,
    notifications.length,
  );

  return (
    <div className="container mx-auto py-6">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">New Teens</h1>
            <p className="text-muted-foreground">
              Manage and respond to new teen assignments
              {totalCount > 0 && (
                <span className="ml-2 text-sm">
                  ({totalCount} total, {filteredNotifications.length}{" "}
                  {showContacted ? "shown" : "new"})
                </span>
              )}
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowContacted(!showContacted)}
            className="flex items-center gap-2"
          >
            {showContacted ? (
              <>
                <EyeOff className="h-4 w-4" />
                Hide Contacted
              </>
            ) : (
              <>
                <Eye className="h-4 w-4" />
                Show Contacted
              </>
            )}
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <NewTeenCardSkeleton key={i} />
          ))}
        </div>
      ) : filteredNotifications.length === 0 ? (
        <div className="py-12 text-center">
          <Users className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-4 text-lg font-medium text-gray-900">
            {showContacted ? "No teens found" : "No new teens"}
          </h3>
          <p className="mt-2 text-sm text-gray-500">
            {showContacted
              ? "No teen assignments found."
              : "You're all caught up! New teen assignments will appear here."}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredNotifications.map((notification) => (
            <NewTeenCard key={notification.id} notification={notification} />
          ))}

          {/* Load More Button */}
          {hasNextPage && (
            <div className="flex justify-center pt-6">
              <Button
                variant="outline"
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
                className="px-8"
              >
                {isFetchingNextPage ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Loading more...
                  </>
                ) : (
                  <>Load More Teens</>
                )}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
